<template>
  <span
    class="p-1 px-2 rounded-full text-xs"
    :class="statusStyle.cssClass"
  >
    {{ displayText }}
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface StatusStyle {
  text: string
  cssClass: string
}

interface UserProgress {
  ahead_tasks_done_count?: number
  backlog_tasks_count?: number
  backlog_tasks_points?: number
}

interface Props {
  status: string
  userProgress?: UserProgress
  showDetails?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showDetails: true
})

const STATUS_CONFIG = {
  AHEAD: {
    text: 'Ahead',
    cssClass: 'bg-lime-200 text-lime-800'
  },
  ON_TRACK: {
    text: 'On Track',
    cssClass: 'bg-cosmicPurple/10 text-cosmicPurple'
  },
  FALLING_BEHIND: {
    text: 'Falling Behind',
    cssClass: 'bg-red-100 text-red-800'
  },
  CATCHING_UP: {
    text: 'Catching Up',
    cssClass: 'bg-yellow-100 text-yellow-800'
  }
} as const

const getDetailedText = (status: string, userProgress?: UserProgress): string => {
  if (!userProgress) return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]?.text || 'Loading...'

  switch (status) {
    case 'AHEAD':
      return userProgress.ahead_tasks_done_count 
        ? `Ahead (${userProgress.ahead_tasks_done_count} future tasks done)`
        : 'Ahead'
    
    case 'FALLING_BEHIND':
    case 'CATCHING_UP':
      if (userProgress.backlog_tasks_count) {
        const taskText = userProgress.backlog_tasks_count > 1 ? 'tasks' : 'task'
        const statusText = status === 'FALLING_BEHIND' ? 'Falling behind' : 'Catching up'
        return `${statusText} (${userProgress.backlog_tasks_count} ${taskText}, ${userProgress.backlog_tasks_points} pts)`
      }
      return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG].text
    
    default:
      return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]?.text || 'Loading...'
  }
}

const statusStyle = computed(() => {
  const config = STATUS_CONFIG[props.status as keyof typeof STATUS_CONFIG]
  return config || { text: 'Loading...', cssClass: 'bg-gray-100 text-gray-800' }
})

const displayText = computed(() => {
  return props.showDetails 
    ? getDetailedText(props.status, props.userProgress)
    : statusStyle.value.text
})

const useStatusStyling = () => {
  const getStatusStyle = (status: string, userProgress?: UserProgress): StatusStyle => {
    const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]
    if (!config) return { text: 'Loading...', cssClass: 'bg-gray-100 text-gray-800' }
    
    return {
      text: getDetailedText(status, userProgress),
      cssClass: config.cssClass
    }
  }

  return { getStatusStyle }
}

defineExpose({ useStatusStyling })
</script>